/**
 * @fileoverview The `client` a PUBLIC marketing entry hands the ServerTransport
 * (UNREACHABLE-MODULE-BURNDOWN-001 Q-UMB-13).
 *
 * Every app bundle ships logs through its own `@puffin-core/api-client` instance. A marketing entry
 * may not: that client is axios, and the `marketingEntryBarrelFreedom` contract keeps it out of the
 * prerendered marketing bundles on measured grounds (the api-client barrel drags axios and a dozen
 * modules into nine SEO-critical entries). Those entries already reach their API through
 * `@puffin-core/api-client/fetch-seam`'s dependency-free `createFetchSeam` — the contact form, the
 * blog CMS and the analytics ingest all do — so this adapts that injected fetch to the one method
 * the transport calls, `post(url, body)`, and to the failure vocabulary its classifier reads:
 *
 * - a network failure rejects as `fetch` rejects (no `status` → transient, retried);
 * - a non-2xx answer rejects with `{ status }` (the api-client's normalised shape);
 * - a 2xx whose body is not JSON rejects as `HtmlResponseError` — the SPA answered, so no ingest is
 *   behind this URL (the api-client's own class of that name carries the same verdict; the
 *   transport disables itself for the page on either).
 *
 * The fetch is INJECTED, never the global: the seam resolves it lazily per call, and this module
 * constructs no transport of its own (`web/no-bespoke-http-transport` exempts exactly this shape).
 *
 * @module transports/fetchLogClient
 */

/**
 * @param {Object} options
 * @param {Function} options.fetch - a fetch implementation (`createFetchSeam()`)
 * @returns {{defaults: {baseURL: string}, post: (url: string, body: Object) => Promise<void>}}
 * @throws {TypeError} when no fetch is injected
 */
export function createFetchLogClient({ fetch } = {}) {
  if (typeof fetch !== 'function') {
    throw new TypeError(
      'createFetchLogClient requires `fetch` — inject `createFetchSeam()` from @puffin-core/api-client/fetch-seam',
    );
  }
  return {
    // The ingest URL a marketing entry pins is already absolute (`/api/v1/telemetry/logs`).
    defaults: { baseURL: '' },
    async post(url, body, { headers = {} } = {}) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(body),
        credentials: 'same-origin',
      });
      if (!response.ok) {
        throw Object.assign(new Error(`client-log ingest answered ${response.status}`), {
          status: response.status,
        });
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw Object.assign(
          new Error(
            `client-log ingest answered ${response.status} with ${contentType || 'no body type'}, not JSON`,
          ),
          { name: 'HtmlResponseError', status: response.status },
        );
      }
    },
  };
}
